<template>
    <div class="uk-margin-bottom">
        <div class="uk-panel" :class="{'lam-lower-contrast': !!wizardShown}">
            <h3 class="uk-panel-title">
                Requests For Materials
                <a v-if="can_create_rfms" href class="uk-button uk-button-link" @click.prevent="create_rfms_confirm">
                    <open-icon glyph="plus" />
                </a>
            </h3>

            <div class="uk-grid uk-grid-small lam-master-detail">
                <div
                    :class="{'uk-width-1-1': !selection, 'uk-width-3-5': !!selection}"
                >
                    <data-table
                        :id="state_identifier"
                        :columns="visible_columns"
                        :rows="filtered_rows"
                        :total="state.total"
                        :found="state.found"
                        :current-page="state.page"
                        :total-pages="nb_pages"
                        :searchable="false"
                        :can-add="can_create_rfms"
                        table-class="lam-subrecords-list"
                        add-button-title="Add a new request for materials"
                        empty-title="No requests for materials"
                        empty-add-button-title="Add request(s) for materials"
                        @click="select_row"
                        @add="create_rfms_confirm"
                        @sort="sort_by"
                        @search="search"
                        @page="goto_page"
                    >

                        <template
                            v-slot:tbody="{ row, field }"
                        >
                            <div v-if="field === 'type'">
                                {{ row.type }}
                                <div v-if="!!card_details(row)" class="uk-text-small">
                                    {{ card_details(row) }}
                                </div>
                            </div>
                            <span v-else-if="field === 'sent_at'" :class="{'uk-text-danger': !card_record(row).sent_at}">{{ card_record(row).sent_at | nibnut.date("MMMM DD, YYYY", "(never)") }}</span>
                            <span v-else-if="!!field.match(/_at$/i)">{{ card_record(row)[field] | nibnut.date("MMMM DD, YYYY") }}</span>
                            <span v-else-if="field === 'status'">{{ status_for_rfm(card_record(row)) }}</span>
                            <span v-else-if="field === 'nb_revisions'">
                                <span v-if="!is_revisionable_rfm_entity(row.entity)">n/a</span>
                                <span v-else>{{ card_record(row).nb_revisions | nibnut.number("0,0", "(none)") }}</span>
                            </span>
                            <div
                                v-else-if="field === 'buttons'"
                                class="uk-form-controls-text uk-text-right"
                            >
                                <open-icon
                                    v-if="!!selection && (selection.entity === row.entity) && (selection.record_id === card_record(row).id)"
                                    glyph="chevron-right"
                                    class="uk-text-warning"
                                />
                            </div>
                            <span v-else>{{ card_record(row)[field] }}</span>
                        </template>
                    </data-table>
                    <div
                        v-if="!!record && (record.number_suffix !== '00') && !!record.scope_sibling_ids && !!record.scope_sibling_ids.length"
                        class="uk-text-warning uk-text-small uk-text-center uk-margin-small-top"
                    >
                        Other RFMs are stored in
                        <router-link
                            :to="{ name: 'project.edit', params: { id: record.scope_sibling_ids[0] } }"
                        >
                            {{ record.number }}.00
                        </router-link>
                    </div>
                </div>
                <div
                    :class="{'uk-width-0': !selection, 'uk-width-2-5': !!selection}"
                    class="lam-cards"
                >
                    <request-for-material-card
                        :card="selection"
                        :editable="can_edit_cards"
                        @service_request_accepted="service_request_accepted"
                        @refresh="refresh_selection"
                        @delete="delete_record"
                    />
                </div>
            </div>
        </div>

        <modal-dialog
            id="create-rfm"
            :show.sync="create"
        >
            <template v-slot:title>
                <span v-if="!!record.activated_at">Add'l Services or Consultant Agreement</span>
                <span v-else>New Request(s) for Material</span>
            </template>

            <div class="uk-grid uk-grid-small" data-uk-grid-margin>
                <div class="uk-width-1-1">
                    <request-for-material-picker
                        v-if="!!create_rfms_data"
                        :record="create_rfms_data"
                        :without-proposal="!can_add_proposal"
                        :with-services="!!record.activated_at"
                    />
                </div>
            </div>

            <div class="uk-modal-footer uk-text-center">
                <div class="uk-grid">
                    <div class="uk-width-1-2">
                        <base-button
                            size="large"
                            width="1-1"
                            :disabled="creating"
                            @click.prevent="create=false"
                            class="uk-width-1-1"
                        >
                            Cancel
                        </base-button>
                    </div>
                    <div class="uk-width-1-2">
                        <base-button
                            :waiting="creating"
                            :disabled="creating"
                            color="primary"
                            size="large"
                            width="1-1"
                            @click.prevent="create_rfms"
                            class="uk-width-1-1"
                        >
                            Save
                        </base-button>
                    </div>
                </div>
            </div>
        </modal-dialog>
    </div>
</template>

<script type="text/javascript">
import { mapGetters } from "vuex"

import is_project_tab_section from "../mixins/IsProjectTabSection"
import is_data_table_source from "../../../mixins/IsDataTableSource"
import project_utilities from "../../../mixins/ProjectUtilities"
import string_utilities from "../../../mixins/StringUtilities"
import requests_for_material_utilities from "../../../mixins/RequestsForMaterialUtilities"
import ui_utilities from "../../../mixins/UiUtilities"

import RequestForMaterialCard from "./RequestForMaterialCard"
import RequestForMaterialPicker from "../../../ui/RequestForMaterialPicker"
import BaseButton from "../../../ui/BaseButton"
import OpenIcon from "../../../ui/OpenIcon"
import ModalDialog from "../../../dialogs/ModalDialog/ModalDialog"

export default {
    mixins: [is_project_tab_section, is_data_table_source, project_utilities, string_utilities, requests_for_material_utilities, ui_utilities],
    components: {
        RequestForMaterialCard,
        RequestForMaterialPicker,
        BaseButton,
        OpenIcon,
        ModalDialog
    },
    mounted () {
        this.load()
    },
    watch: {
        $route: "load",
        profile_id: "load",
        "record.id": "load"
    },
    methods: {
        load () {
            if(this.profile_id && this.record && this.record.id) {
                this.rfm_entities.forEach((entity, index) => {
                    this.$store.dispatch("FETCH_RECORDS", { entity, query: { project_id: this.record.id, relation_ids: ["attachment"] } }).then(() => {
                        if(index >= (this.rfm_entities.length - 1)) {
                            this.$nextTick(() => {
                                const rows = this.rows
                                if(rows.length) this.select_row(rows[0])
                            })
                        }
                    })
                })
            }
        },
        card_record (card) {
            return this.entity_record(card.entity, card.record_id)
        },
        card_details (card) {
            const record = this.card_record(card)

            let details = record.description
            if(card.entity === "qualification") {
                if(!!record.qualification_type_ids && !!record.qualification_type_ids.length) {
                    return this.qualification_types.filter(qualification_type => {
                        return record.qualification_type_ids.indexOf(qualification_type.id) >= 0
                    }).map(qualification_type => qualification_type.name).join(", ")
                }
            } else if(card.entity === "proposal") {
                const tokens = []
                if(record.computed_proposal_type) tokens.push(record.computed_proposal_type)
                if(record.computed_fee) tokens.push(`$ ${this.nibnut_filter("nibnut.number", [record.computed_fee + record.computed_subcontractors_fee, "0,0"])}`)
                details = tokens.join(", ")
            } else if(card.entity === "contract") {
                const tokens = []
                if(record.computed_contract_type) tokens.push(record.computed_contract_type)
                if(record.computed_decision) {
                    const decision = this.agreement_decisions.find(agreement_decision => agreement_decision.id === record.computed_decision)
                    if(decision) tokens.push(decision.name)
                }
                details = tokens.join(", ")
            } else if(card.entity === "fee_estimate") {
                if(record.computed_fee_type) details = record.computed_fee_type
            }

            return details
        },
        select_row (row) {
            this.selection = row
        },
        refresh_selection (entity, id) {
            this.selection.record_id = id
        },
        delete_record () {
            if(this.selection) {
                this.$confirm({
                    type: "error",
                    title: `Delete ${this.selection.type}`,
                    message: `Do you really want to delete this ${this.selection.type}? Once it's gone, it's gone...`,
                    cancel: "Keep It",
                    ok: "Delete It",
                    then: (confirmed) => {
                        if(confirmed) {
                            this.$store.dispatch("RECORD_DELETE", {
                                entity: this.selection.entity,
                                id: this.selection.record_id
                            }).then(() => {
                                this.selection = this.filtered_rows.length ? this.filtered_rows[0] : null
                            }).catch(error => {
                                if(error) this.$error(error.message)
                            })
                        }
                    }
                })
            }
        },
        create_rfms_confirm () {
            this.create_rfms_data.requests_for_material = []
            this.create_rfms_data.requested_at = ""
            this.create_rfms_data.due_at = ""
            this.create = true
        },
        create_rfms () {
            if(this.can_create_rfms) {
                this.creating = true
                this.$store.dispatch("CREATE_REQUESTS_FOR_MATERIAL", { id: this.record.id, data: this.create_rfms_data }).then(updates => {
                    const entities = Object.keys(updates)
                    if(entities && entities.length) {
                        for(let loop = 0; loop < entities.length; loop++) {
                            const entity_updates = updates[entities[loop]]
                            if(entity_updates.length) {
                                this.selection = this.rows.find(card => (card.entity === entities[loop]) && (card.record_id === entity_updates[0].id))
                                break
                            }
                        }
                    }
                    this.create = false
                }).catch(error => {
                    this.$error(error.message)
                }).then(() => {
                    this.creating = false
                })
            }
        },
        service_request_accepted () {
            if(this.im_pic_or_pm) {
                this.$nextTick(() => {
                    this.$success("Don't forget! You defined the additional services' phase/task as 'Multiple' - so you need to set these up manually.")
                    this.scroll_to("#prospective-phases")
                })
            } else {
                this.$success("PM notified. They will need to set-up the phases/tasks relating to this additional service.")
            }
        }
    },
    computed: {
        ...mapGetters(["entity_records", "entity_record"]),
        state_identifier () {
            return `rfms-${this.record.id}`
        },
        fields () {
            const fields = Object.keys(this.columns)
            fields.push("agreement_type")
            return fields
        },
        rows () {
            const rows = []
            if(this.record && this.record.id) {
                this.dictionary("request_for_material")

                this.rfm_entities.forEach(entity => {
                    const type = (entity === "service_request") ? "Additional Services" : this.term_value_by(entity, "request_for_material")

                    this.entity_records(entity).filter(rfm_record => {
                        if((entity !== "proposal") || !this.record.activated_at) return (rfm_record.project_id === this.record.id)
                        if(this.record.number_suffix === "00") return (rfm_record.project_id === this.record.id) && !rfm_record.agent_of
                        return !rfm_record.agent_of
                    }).forEach(rfm_record => {
                        let row_type = type
                        if(entity === "material_request") row_type = rfm_record.request_type
                        else if(entity === "contract") row_type = rfm_record.agreement_type
                        rows.push({
                            type: row_type,
                            entity,
                            record_id: rfm_record.id
                        })
                    })
                })
            }

            if(this.state.total !== rows.length) {
                this.set_state_values({
                    total: rows.length,
                    found: rows.length
                })
            }

            return rows
        },
        can_create_rfms () {
            return !!this.canAdd && (!this.record.activated_at || !!this.can_edit_admin_data || !!this.is_business_manager)
        },
        can_edit_admin_data () {
            if(this.is_db_admin_active) return true
            else if(!this.record) return false
            if(this.project_is_overhead(this.record)) return true
            if(this.is_some_designer || this.is_associate || this.is_senior_associate) return this.editable && this.im_pic_or_pm
            return this.editable
        },
        can_edit_cards () {
            if(this.is_db_admin_active) return true
            if(!this.record) return false
            if(this.record.activated_at) return this.project_is_overhead(this.record) || (this.editable && this.im_pic_or_pm) || this.is_administrator || this.is_business_manager
            return this.editable
        },
        qualification_types () {
            return this.dictionary("qualification_types", "name")
        },
        agreement_decisions () {
            return this.dictionary("agreement_decisions", "name")
        },
        can_add_proposal () {
            // Cheryl wants to be able to add add'l proposals to scoped projects
            return true
            /*
            const proposals = this.entity_records("proposal").filter(proposal => {
                return proposal.project_id === this.record.id
            })
            return !proposals.length || !this.entity_records("proposal_revision").find(proposal_revision => {
                return (proposal_revision.project_id === this.record.id) && !!proposal_revision.scopes
            })
            */
        }
    },
    props: {
        canAdd: {
            type: Boolean,
            default: false
        }
    },
    data () {
        return {
            default_state: {
                per_page: 10,
                page: 1,
                sort_by: "due_at",
                sort_dir: "asc",
                filter_on: null,
                filter: null,
                archived: false,
                search: "",
                total: 0,
                found: 0,
                query_info: {}
            },

            columns: {
                type: { label: "Type", sort: null, type: "plain" },
                requested_at: { label: "Received", sort: null, type: "plain" },
                due_at: { label: "Due", sort: null, type: "plain" },
                sent_at: { label: "Sent", sort: null, type: "plain" },
                status: { label: "Status", sort: false, type: "plain" },
                nb_revisions: { label: "Revisions", sort: false, type: "plain", cell_class: "lam-col-user-initials uk-text-center" },
                buttons: { label: " ", sort: false, type: "plain", cell_class: "lam-col-user-initials" }
            },
            selection: null,

            create_rfms_data: {
                requests_for_material: [],
                requested_at: "",
                due_at: ""
            },
            create: false,
            creating: false
        }
    }
}
</script>
